// RevenueCat purchases — accesare prin Capacitor.Plugins la runtime.
//
// De ce NU importam { Purchases } direct:
//   Purchases e un Proxy Capacitor. Daca accesezi `.then` pe el (ex: await,
//   verificari de thenable din webpack sau biblioteci), Capacitor incearca sa
//   apeleze o metoda nativa `then` care nu exista -> "Purchases.then() is not
//   implemented on android".
//
//   Importand side-effect (doar ca sa ruleze registerPlugin) si citind proxy-ul
//   din Capacitor.Plugins doar INAUNTRUL functiilor noastre async, niciun tool
//   de bundling / ESM nu mai poate ajunge la el ca la un export "thenable".

import '@revenuecat/purchases-capacitor'; // side-effect: registerPlugin('Purchases', ...)
import { LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { isNative, isAndroid, isIOS } from '../utils/platform';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/tokenManager';
import { logger } from '../utils/logger';

const RC_GOOGLE_KEY = process.env.REACT_APP_RC_ANDROID_API_KEY || 'goog_ajaeDdUEQlIaioBwKnTzbxBokPA';
const RC_APPLE_KEY  = process.env.REACT_APP_RC_IOS_API_KEY || '';

/**
 * Loguri explicite pentru diagnostic (inclusiv production / TestFlight).
 * Nu folosim logger.info — în production acolo e no-op.
 */
const rcDiagWarn = (msg, payload) => {
  const label = `[RC-DIAG] ${msg}`;
  if (payload === undefined) {
    console.warn(label);
    return;
  }
  try {
    console.warn(
      label,
      typeof payload === 'string'
        ? payload
        : JSON.stringify(payload, (_k, v) => (typeof v === 'bigint' ? String(v) : v), 2),
    );
  } catch {
    console.warn(label, payload);
  }
};

const summarizeOffering = (off) => {
  if (!off || typeof off !== 'object') return null;
  const pkgs = Array.isArray(off.availablePackages) ? off.availablePackages : [];
  return {
    identifier: off.identifier ?? null,
    monthlyPresent: !!off.monthly,
    annualPresent: !!(off.annual || off.yearly),
    availablePackagesCount: pkgs.length,
    packages: pkgs.map((p) => ({
      identifier: p?.identifier ?? null,
      packageType: p?.packageType ?? null,
      productIdentifier: p?.product?.identifier ?? null,
      price: p?.product?.price ?? null,
      priceString: p?.product?.priceString ?? null,
      currencyCode: p?.product?.currencyCode ?? null,
    })),
  };
};

// Accesor lazy la proxy. Nu se apeleaza la load time, doar in interiorul
// functiilor async -> webpack nu mai poate sa faca thenable-check pe el.
const rc = () => {
  const P = Capacitor?.Plugins?.Purchases;
  if (!P) throw new Error('RC plugin not registered on Capacitor.Plugins');
  return P;
};

// SDK-ul RC pastreaza persistent app_user_id pe device intre sesiuni.
// configure() e no-op dupa prima rulare, deci NU schimba user-ul.
// Trebuie sa folosim logIn()/logOut() explicit pentru a schimba identitatea.
let rcConfigured = false;

/** Pentru UI / suport: cheile publice RC trebuie să fie în bundle la build (REACT_APP_RC_*). */
export const getRevenueCatBuildStatus = () => ({
  nativePlatform: Capacitor.getPlatform(),
  hasIosPublicApiKey: typeof RC_APPLE_KEY === 'string' && RC_APPLE_KEY.trim().length >= 8,
  hasAndroidPublicApiKey: typeof RC_GOOGLE_KEY === 'string' && RC_GOOGLE_KEY.trim().length >= 8,
});

/**
 * Inițializează SDK-ul RevenueCat pe native.
 * @returns {{ ok: boolean, reason?: string, platform?: string, message?: string }}
 */
export const initPurchases = async (userId) => {
  if (!isNative()) return { ok: false, reason: 'not_native' };
  if (!userId) {
    rcDiagWarn('initPurchases skipped: missing userId', {
      nativePlatform: Capacitor.getPlatform(),
    });
    console.warn('[RC] init skipped: missing userId');
    return { ok: false, reason: 'no_user' };
  }
  try {
    const apiKey = isAndroid() ? RC_GOOGLE_KEY : RC_APPLE_KEY;
    if (!apiKey) {
      rcDiagWarn('initPurchases skipped: missing platform API key', {
        nativePlatform: Capacitor.getPlatform(),
        isAndroid: isAndroid(),
        isIOS: isIOS(),
        hasAndroidKey: Boolean(RC_GOOGLE_KEY),
        hasIosKey: Boolean(RC_APPLE_KEY),
      });
      console.warn('[RC] init skipped: missing platform API key.');
      return {
        ok: false,
        reason: 'no_api_key',
        platform: isIOS() ? 'ios' : isAndroid() ? 'android' : Capacitor.getPlatform(),
      };
    }
    const Purchases = rc();
    const lvl = process.env.NODE_ENV === 'production' ? LOG_LEVEL.WARN : LOG_LEVEL.DEBUG;

    if (!rcConfigured) {
      await Purchases.setLogLevel({ level: lvl });
      rcDiagWarn('configure: starting first configure()', {
        nativePlatform: Capacitor.getPlatform(),
        apiKeyPrefix: typeof apiKey === 'string' ? apiKey.slice(0, 5) : null,
        appUserIDLength: userId ? String(userId).length : 0,
      });
      await Purchases.configure({ apiKey, appUserID: userId });
      rcConfigured = true;
      logger.info('[RC] configure OK');
      rcDiagWarn('configure: OK');
      return { ok: true, reason: 'configured' };
    }

    // SDK deja configurat — verificam daca app_user_id curent corespunde user-ului
    // real din Supabase. Daca nu, facem logIn ca sa reatasam plati viitoare corect.
    let currentAppUserId = null;
    try {
      const info = await Purchases.getAppUserID();
      currentAppUserId = info?.appUserID || info || null;
    } catch {}

    if (currentAppUserId && currentAppUserId === userId) {
      rcDiagWarn('configure: SDK already configured, app user matches — no logIn', {
        appUserIDLength: userId ? String(userId).length : 0,
      });
      return { ok: true, reason: 'already_same_user' };
    }

    await Purchases.logIn({ appUserID: userId });
    logger.info('[RC] logIn OK (app_user_id updated)');
    rcDiagWarn('logIn: OK (app_user_id updated)', {
      hadPreviousId: !!currentAppUserId,
    });
    return { ok: true, reason: 'logged_in' };
  } catch (err) {
    rcDiagWarn('initPurchases threw', {
      message: err?.message || String(err),
      code: err?.code,
      nativePlatform: Capacitor.getPlatform(),
    });
    console.warn('[RC] init failed:', err?.message || err);
    return {
      ok: false,
      reason: 'error',
      message: err?.message ? String(err.message) : String(err),
    };
  }
};

export const logoutPurchases = async () => {
  if (!isNative()) return;
  if (!rcConfigured) return;
  try {
    const Purchases = rc();
    await Purchases.logOut();
    logger.info('[RC] logOut OK — SDK reset to anonymous ID');
  } catch (err) {
    const msg = String(err?.message || '').toLowerCase();
    if (msg.includes('anonymous')) return;
    console.warn('[RC] logOut failed:', err?.message || err);
  }
};

export const getOfferings = async () => {
  if (!isNative()) return null;
  try {
    const Purchases = rc();
    rcDiagWarn('getOfferings: invoking native Purchases.getOfferings()', {
      nativePlatform: Capacitor.getPlatform(),
      rcConfigured,
      hasAndroidKey: Boolean(RC_GOOGLE_KEY),
      hasIosKey: Boolean(RC_APPLE_KEY),
    });
    const result = await Purchases.getOfferings();
    try {
      rcDiagWarn(
        'getOfferings: raw native response',
        typeof result === 'string' ? result : JSON.parse(JSON.stringify(result)),
      );
    } catch {
      rcDiagWarn('getOfferings: raw native response (non-serializable), logging keys only', {
        type: typeof result,
        keys:
          result && typeof result === 'object'
            ? Object.keys(result)
            : [],
      });
    }

    // RC Capacitor poate intoarce { offerings: { current, all } } SAU { current, all }.
    const inner = result?.offerings ?? result;
    const current = inner?.current;
    const all     = inner?.all;
    const allKeys = all && typeof all === 'object' ? Object.keys(all) : [];

    rcDiagWarn('getOfferings: parsed container', {
      hasInner: !!inner,
      hasCurrent: !!current,
      currentOfferingId: current?.identifier ?? null,
      currentSummary: summarizeOffering(current),
      allOfferingKeys: allKeys,
      allOfferingSummaries: allKeys.reduce((acc, key) => {
        acc[key] = summarizeOffering(all[key]);
        return acc;
      }, {}),
      chosenOfferingId:
        current?.identifier
        ?? (allKeys.length === 1 ? allKeys[0] : null),
    });

    const pickDefaultFromAll = () => {
      if (!all || typeof all !== 'object') return null;
      const d = all.default ?? all.DEFAULT;
      if (d && typeof d === 'object') return d;
      if (!allKeys.length) return null;
      const exact = Object.values(all).find(
        (o) => typeof o?.identifier === 'string' && /^default$/i.test(o.identifier),
      );
      if (exact) return exact;
      if (allKeys.length === 1) return Object.values(all)[0];
      return null;
    };

    const pickLegacyFirstInAll = () => {
      if (!all || typeof all !== 'object') return null;
      const vals = Object.values(all);
      return vals.length ? vals[0] : null;
    };

    let offering = current ?? pickDefaultFromAll();

    // Ultimul pas = comportamentul vechi din cod: `current || Object.values(all)[0]`.
    // După ce încercăm explicit `all.default` / identifier „default” / offering unic.
    if (!offering) {
      offering = pickLegacyFirstInAll();
      if (offering && !current && allKeys.length > 1) {
        rcDiagWarn(
          'getOfferings: LEGACY FALLBACK — `current` gol; folosesc primul element din `all` (mai multe chei). Marchează offering-ul corect ca „current” în RevenueCat.',
          summarizeOffering(offering),
        );
      }
    }

    if (!current && offering) {
      rcDiagWarn(
        'getOfferings: `current` was null — using fallback offering',
        summarizeOffering(offering),
      );
    }

    if (!offering) {
      rcDiagWarn(
        'getOfferings: no offering usable (current null și fallback indisponibil)',
        {
          hint:
            'Verifică în RC: offering marcat „current”, sau cheia „default” în all, sau un singur offering în all.',
        },
      );
    }

    return offering || null;
  } catch (err) {
    rcDiagWarn('getOfferings: native threw', {
      message: err?.message || String(err),
      code: err?.code,
      nativePlatform: Capacitor.getPlatform(),
    });
    console.warn('[RC] getOfferings failed:', err?.message || err);
    return null;
  }
};

export const purchasePackage = async (pkg) => {
  const Purchases = rc();

  if (isAndroid()) {
    if (!pkg?.product) throw new Error('Pachet invalid: produsul lipseste.');

    try {
      const status = await Purchases.canMakePayments({});
      logger.info('[RC] canMakePayments(Android):', status);
    } catch (e) {
      console.warn('[RC] canMakePayments check failed:', e?.message || e);
    }

    logger.info('[RC] Android purchase product:', {
      packageIdentifier: pkg.identifier,
      productIdentifier: pkg.product?.identifier,
      title: pkg.product?.title,
    });

    const { customerInfo } = await Purchases.purchaseStoreProduct({ product: pkg.product });
    return customerInfo;
  }

  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    return customerInfo;
  } catch (err) {
    const msg = String(err?.message || '').toLowerCase();
    if (msg.includes('not implemented') && msg.includes('android') && pkg?.product) {
      console.warn('[RC] purchasePackage unavailable on Android bridge, fallback.');
      const { customerInfo } = await Purchases.purchaseStoreProduct({ product: pkg.product });
      return customerInfo;
    }
    throw err;
  }
};

export const restorePurchases = async () => {
  if (!isNative()) return null;
  try {
    const Purchases = rc();
    const { customerInfo } = await Purchases.restorePurchases();
    return customerInfo;
  } catch (err) {
    console.warn('[RC] restorePurchases failed:', err?.message || err);
    return null;
  }
};

export const checkPremiumStatus = async () => {
  if (!isNative()) return null;
  try {
    const Purchases = rc();
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo.entitlements.active['premium'] !== undefined;
  } catch (err) {
    console.warn('[RC] checkPremiumStatus failed:', err?.message || err);
    return null;
  }
};

/**
 * Cheama backend-ul /api/subscriptions/sync pentru a forta update-ul statusului
 * premium in Supabase, imediat dupa o plata reusita (fara sa astepte webhook-ul).
 * Returneaza { synced, premium, premium_until } sau null daca eroare.
 */
export const syncSubscriptionStatus = async () => {
  try {
    const res = await fetch(`${getApiBaseUrl()}/api/subscriptions/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    if (!res.ok) {
      console.warn('[sync] HTTP', res.status);
      return null;
    }
    const data = await res.json();
    logger.info('[sync] result:', data);
    return data;
  } catch (err) {
    console.warn('[sync] request failed:', err?.message || err);
    return null;
  }
};

/**
 * Detecteaza daca o eroare aruncata de RC inseamna "user a anulat paywall-ul".
 *
 * RevenueCat Capacitor pe Android arunca:
 *   - err.code = 1 (numeric) SAU "1" (string) pentru PURCHASE_CANCELLED_ERROR
 *   - err.userCancelled = true
 *   - err.message contine cuvantul "cancel"
 *
 * Pe iOS:
 *   - err.code = "PURCHASE_CANCELLED" (string)
 *   - err.userCancelled = true
 *
 * Plus din SDK-ul vechi (PurchaseCancelledError): string "PURCHASE_CANCELLED".
 */
export const isPurchaseCancelledError = (err) => {
  if (!err) return false;
  if (err.userCancelled === true) return true;
  const code = err.code;
  if (code === 'PURCHASE_CANCELLED') return true;
  if (code === 1 || code === '1') return true;
  const msg = String(err.message || '').toLowerCase();
  if (/user\s*cancel|purchase\s*cancel|cancell?ed/.test(msg)) return true;
  return false;
};

export const getCustomerInfo = async () => {
  if (!isNative()) return null;
  try {
    const Purchases = rc();
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo;
  } catch (err) {
    console.warn('[RC] getCustomerInfo failed:', err?.message || err);
    return null;
  }
};
